import { useState, type JSX } from 'react';
import { FormProvider, type FieldValues } from 'react-hook-form';
import { toast } from '@/lib/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import {
  companyFormFields,
  companyFormSchema,
  companyFormSections,
  companyFieldOrder,
} from '../../components/companyFormSchema';
import type { CompanyWithSites } from '@/data/repos/companiesRepo';
import { useUnifiedForm } from '../../../../hooks/useUnifiedForm';
import {
  EntityDialog,
  EntityDialogFooter,
  EntityFormFields,
  FormErrorBanner,
  FormErrorSummary,
  useEntityDialogForm,
  useEntityDialogSubmit,
} from '@/components/blocks/forms';
import { Button, DialogClose } from '@/components/ui';
import { scrollToFirstError } from '@/components/blocks/forms/formErrors';
import { ConfirmDialog } from '@/components/blocks/feedback/ConfirmDialog';
import { CompanyLogoField } from '../../components/CompanyLogoField';
import { useDataProvider } from '@/data/provider';

interface EditCompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyWithSites | null;
  onUpdated?: (company: CompanyWithSites | null) => void;
  onDelete?: () => void;
  isDeleting?: boolean;
}

export function EditCompanyModal({
  open,
  onOpenChange,
  company,
  onUpdated,
  onDelete,
  isDeleting = false,
}: EditCompanyModalProps): JSX.Element {
  const queryClient = useQueryClient();
  const { companies } = useDataProvider();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { fields } = useUnifiedForm({
    schema: companyFormSchema,
    fields: companyFormFields,
    fieldOrder: companyFieldOrder,
    fieldOverrides: {
      notes: { colSpan: 2 },
    },
  });

  const updateCompany = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      if (!company) throw new Error('Company not available');
      const logoUrl =
        typeof values.logoUrl === 'string' && values.logoUrl.trim().length
          ? values.logoUrl.trim()
          : undefined;
      return companies.saveCompany({
        id: company.id,
        name: values.name as string,
        code: values.code as string | undefined,
        vat: values.vat as string | undefined,
        sedeLegaleAddress: values.sedeLegaleAddress as string | undefined,
        sedeLegaleCap: values.sedeLegaleCap as string | undefined,
        sedeLegaleCity: values.sedeLegaleCity as string | undefined,
        sedeLegaleProvince: values.sedeLegaleProvince as string | undefined,
        sedeLegale: undefined,
        notes: values.notes as string | undefined,
        logoUrl,
        createdAt: company.createdAt,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.all }),
        company
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.companies.detail(company.id),
            })
          : Promise.resolve(),
      ]);
      const refreshed = company ? await companies.getCompany(company.id) : null;
      toast.success('Azienda aggiornata');
      onUpdated?.(refreshed ?? company);
      onOpenChange(false);
    },
  });

  const data = company
    ? {
        name: company.name,
        code: company.code ?? '',
        vat: company.vat ?? '',
        sedeLegaleAddress: company.sedeLegaleAddress ?? '',
        sedeLegaleCap: company.sedeLegaleCap ?? '',
        sedeLegaleCity: company.sedeLegaleCity ?? '',
        sedeLegaleProvince: company.sedeLegaleProvince ?? '',
        notes: company.notes ?? '',
        logoUrl: company.logoUrl ?? '',
      }
    : null;

  const { form, formId, errorCount, rootError, handleInvalidSubmit, setRootError } =
    useEntityDialogForm({
      open,
      schema: companyFormSchema,
      fields,
      data,
    });

  const handleSave = useEntityDialogSubmit({
    form,
    onSubmit: async (values: FieldValues) => {
      await updateCompany.mutateAsync(values);
    },
    setRootError,
  });

  if (!company) {
    return <></>;
  }

  const handleArchive = async () => {
    if (!onDelete) return;
    try {
      await onDelete();
      setConfirmOpen(false);
    } catch (error) {
      setRootError('Impossibile archiviare. Riprova o contatta l’assistenza.');
      throw error;
    }
  };

  const isSaving = updateCompany.isPending;
  const isDirty = form.formState.isDirty;

  return (
    <>
      <EntityDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Modifica azienda"
        size="sm"
        isDirty={isDirty}
        isSaving={isSaving}
        formId={formId}
        banner={rootError ? <FormErrorBanner message={rootError} /> : null}
        summary={
          errorCount > 0 ? (
            <FormErrorSummary
              count={errorCount}
              onJumpToFirstError={() => scrollToFirstError(form.formState.errors)}
            />
          ) : null
        }
        footer={
          <EntityDialogFooter
            extra={
              onDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setConfirmOpen(true)}
                  disabled={isSaving || isDeleting}
                >
                  {isDeleting ? 'Archiviazione…' : 'Archivia azienda'}
                </Button>
              ) : null
            }
            secondary={
              <DialogClose asChild>
                <Button variant="ghost" disabled={isSaving || isDeleting}>
                  Chiudi
                </Button>
              </DialogClose>
            }
            primary={
              <Button type="submit" form={formId} disabled={!isDirty || isSaving || isDeleting}>
                {isSaving ? 'Salvataggio…' : 'Salva'}
              </Button>
            }
          />
        }
      >
        <FormProvider {...form}>
          <form
            id={formId}
            onSubmit={form.handleSubmit(handleSave, handleInvalidSubmit)}
            noValidate
            className="space-y-4"
          >
            <CompanyLogoField disabled={isSaving || isDeleting} />
            <EntityFormFields
              fields={fields}
              sections={companyFormSections}
              disabled={isSaving || isDeleting}
            />
          </form>
        </FormProvider>
      </EntityDialog>

      {onDelete ? (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Archiviare azienda?"
          message="L’elemento verrà archiviato e non sarà più visibile nelle liste."
          cancelLabel="Annulla"
          confirmLabel="Archivia"
          variant="danger"
          onConfirm={handleArchive}
          isLoading={isDeleting}
        />
      ) : null}
    </>
  );
}
